import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Mic2, SlidersHorizontal, Wand2 } from "lucide-react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { isPublicPageListed, mergeSiteContent } from "../utils/helpers";
import MediaRenderer from "../components/MediaRenderer";
import { typographyFor } from "../lib/typography";

const serviceIcons = [Mic2, SlidersHorizontal, Wand2];

function serviceHref(title = "") {
  const normalized = title.toLowerCase();
  if (normalized.includes("mix")) return "/services/mixage";
  if (normalized.includes("master")) return "/services/mastering";
  if (normalized.includes("prod")) return "/services/prod";
  return "/reservation-studio";
}

export default function StudioPage() {
  const [site, setSite] = useState(() => mergeSiteContent());

  useEffect(() => {
    const off = onSnapshot(
      doc(db, "admin", "content"),
      (snap) => setSite(mergeSiteContent(snap.exists() ? snap.data() : {})),
      (err) => console.error("Erreur Firestore Studio:", err)
    );
    return () => off();
  }, []);

  const page = site.pages.studio;
  const bookingUrl = page.ctaUrl && page.ctaUrl !== "/contact" ? page.ctaUrl : "/reservation-studio";
  const showBooking = isPublicPageListed(site, "booking");
  const showServices = isPublicPageListed(site, "services");
  const services = useMemo(
    () => (page.services || []).filter((service) => !service.hidden),
    [page.services]
  );
  const methodSteps = useMemo(
    () => (page.methodSteps || []).filter((step) => !step.hidden),
    [page.methodSteps]
  );

  return (
    <main className="min-h-screen bg-black text-white">
      {site.visibility.studioHero && (
      <section className="relative min-h-[78vh] overflow-hidden px-6 pt-24 md:px-20 md:pt-32">
        <MediaRenderer
          src={page.heroVideo || page.heroImage}
          type={page.heroVideo ? "video" : "image"}
          fallback={page.heroImage || "/images/studio2.jpg"}
          alt=""
          background
          className="absolute inset-0 h-full w-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/75 to-black" />
        <div className="relative mx-auto flex max-w-7xl flex-col justify-end pb-16 md:min-h-[70vh]">
          <p className="mb-4 text-sm font-semibold uppercase tracking-[0.25em] text-red-500">
            {page.eyebrow}
          </p>
          <h1 className="max-w-5xl text-5xl font-black uppercase leading-none md:text-8xl" style={typographyFor(site, "studioTitle")}>
            {page.title}
          </h1>
          <div className="mt-8 flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <p className="readable-copy max-w-2xl text-lg text-gray-300" style={typographyFor(site, "studioText")}>
              {page.description}
            </p>
            {showBooking && (
              <Link
                to={bookingUrl}
                className="inline-flex w-fit rounded-full bg-white px-6 py-3 text-sm font-bold uppercase tracking-wide text-black transition hover:bg-red-600 hover:text-white"
              >
                {page.ctaLabel || "Reserver une seance"}
              </Link>
            )}
          </div>
        </div>
      </section>
      )}

      {site.visibility.studioServices && showServices && services.length > 0 && (
      <section className="px-6 py-20 md:px-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-5 md:grid-cols-3">
          {services.map(({ title, text, image }, index) => {
            const Icon = serviceIcons[index % serviceIcons.length];
            return (
            <Link key={title} to={serviceHref(title)} className="group overflow-hidden rounded-lg border border-white/10 bg-zinc-950 transition hover:-translate-y-1 hover:border-red-500/60">
              <div className="aspect-[4/3] overflow-hidden bg-zinc-900">
                <MediaRenderer
                  src={image}
                  type="auto"
                  alt={title}
                  background
                  className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <div className="mb-5 flex h-11 w-11 items-center justify-center rounded-full bg-white text-black">
                  <Icon size={20} />
                </div>
                <h2 className="text-2xl font-bold" style={typographyFor(site, "studioTitle")}>{title}</h2>
                <p className="readable-copy mt-3 text-gray-400" style={typographyFor(site, "studioText")}>{text}</p>
              </div>
            </Link>
            );
          })}
        </div>
      </section>
      )}

      {site.visibility.studioMethod && methodSteps.length > 0 && (
      <section className="border-y border-white/10 bg-zinc-950 px-6 py-16 md:px-20">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-8 md:grid-cols-[0.8fr_1.2fr] md:items-center">
          <div>
            <p className="mb-3 text-sm font-semibold uppercase tracking-[0.25em] text-red-500">
              {page.methodEyebrow}
            </p>
            <h2 className="text-4xl font-black uppercase md:text-6xl" style={typographyFor(site, "studioTitle")}>
              {page.methodTitle}
            </h2>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {methodSteps.map((step, index) => (
              <div key={`${step.title}-${index}`} className="rounded-lg border border-white/10 bg-black p-5">
                <span className="text-sm font-bold text-red-500">0{index + 1}</span>
                <h3 className="mt-8 text-xl font-bold">{step.title}</h3>
                <p className="readable-copy mt-2 text-sm text-gray-500" style={typographyFor(site, "studioText")}>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}
    </main>
  );
}
